import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export const BANK_TYPES = ["commercial", "islamic", "foreign", "specialized"] as const;
export type BankTypeValue = (typeof BANK_TYPES)[number];

export interface BankRecord {
  id: string;
  name: string;
  shortName: string;
  code: string;
  swiftCode: string;
  bankType: BankTypeValue;
  website: string;
  logo: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BankDraft = {
  name: string;
  shortName: string;
  code: string;
  swiftCode: string;
  bankType: BankTypeValue;
  isActive: boolean;
  logo: string;
  logoFile?: File | null;
  removeLogo?: boolean;
};

const LS_KEY = "inventory.banks.v1";

type BankApiRecord = {
  id?: number | string;
  name?: string | null;
  short_name?: string | null;
  code?: string | null;
  swift_code?: string | null;
  bank_type?: string | null;
  website?: string | null;
  logo?: string | null;
  sort_order?: number | string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = { message?: string; data?: BankApiRecord };
type DeleteApiResponse = { message?: string };

function dedupeById(items: BankRecord[]): BankRecord[] {
  const byId = new Map<ID, BankRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function normalizeBankType(raw: unknown): BankTypeValue {
  const v = String(raw ?? "").toLowerCase();
  if ((BANK_TYPES as readonly string[]).includes(v)) return v as BankTypeValue;
  return "commercial";
}

function extractRowsFromResponse(body: unknown): BankApiRecord[] {
  if (Array.isArray(body)) return body as BankApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): BankApiRecord[] | null => {
    if (Array.isArray(block)) return block as BankApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as BankApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "banks", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): BankApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") return block as unknown as BankApiRecord;
  }
  if ("id" in root && typeof root.name === "string") return root as unknown as BankApiRecord;
  return null;
}

function mapApiRecord(record: BankApiRecord): BankRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  const sortRaw = record.sort_order;
  const sortOrder = sortRaw == null || sortRaw === "" ? 0 : Math.max(0, Number(sortRaw) || 0);
  return {
    id: String(record.id ?? ""),
    name: str(record.name),
    shortName: str(record.short_name),
    code: str(record.code),
    swiftCode: str(record.swift_code),
    bankType: normalizeBankType(record.bank_type),
    website: str(record.website),
    logo: str(record.logo),
    sortOrder,
    isActive: record.is_active !== false,
    createdAt: created,
    updatedAt: updated,
  };
}

function buildFormData(draft: BankDraft, opts?: { methodPut?: boolean }): FormData {
  const form = new FormData();
  if (opts?.methodPut) form.append("_method", "PUT");
  form.append("name", draft.name.trim());
  form.append("short_name", draft.shortName.trim());
  form.append("code", draft.code.trim());
  form.append("swift_code", draft.swiftCode.trim());
  form.append("bank_type", draft.bankType);
  form.append("is_active", draft.isActive ? "1" : "0");
  if (draft.logoFile) form.append("logo", draft.logoFile);
  else if (draft.removeLogo) form.append("remove_logo", "1");
  return form;
}

function load(): BankRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BankRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: BankRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

const PAGE_SIZE = 20;

export function bankTypeLabel(type: BankTypeValue): string {
  switch (type) {
    case "islamic":
      return "Islamic";
    case "foreign":
      return "Foreign";
    case "specialized":
      return "Specialized";
    default:
      return "Commercial";
  }
}

export function bankApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

export function listBanks(): BankRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function syncBanksFromApi(): Promise<BankRecord[]> {
  try {
    const collected: BankApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/banks", { params: { page, per_page: PAGE_SIZE } });
      collected.push(...extractRowsFromResponse(response.data));
      lastPage = readLastPageFromListBody(response.data) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);
    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listBanks();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) throw error;
    console.error("[Banks] sync failed", error);
    return listBanks();
  }
}

export async function createBank(draft: BankDraft): Promise<{ record: BankRecord; message: string }> {
  const items = load();
  const response = await api.post<UpsertApiResponse>("/api/v1/banks", buildFormData(draft), {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create bank response.");
  const record = mapApiRecord(created);
  const idx = items.findIndex((b) => b.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Bank created successfully." };
}

export async function updateBank(
  id: ID,
  draft: BankDraft,
): Promise<{ record: BankRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((b) => b.id === id);
  if (idx === -1) return null;
  const response = await api.post<UpsertApiResponse>(
    `/api/v1/banks/${id}`,
    buildFormData(draft, { methodPut: true }),
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const updated = extractFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update bank response.");
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Bank updated successfully." };
}

export async function deleteBank(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/banks/${id}`);
  const items = load();
  const next = items.filter((b) => b.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Bank deleted successfully." };
}
